import { ArrowLeft, DotsVertical, Share01 } from '@untitledui/icons';

import { useBreakpoint } from '@/hooks/useBreakpoint';
import { Button } from '@/lib/base/buttons/button';
import { Dropdown } from '@/lib/base/overlays/dropdown';

import { TypewriterTitle } from '../reasoningMessageHelpers';

export type ChatTab = 'conversation' | 'materials';

interface ChatThreadHeaderProps {
  title: string | undefined;
  activeTab: ChatTab;
  onNavigateBack: () => void;
  onToggleMaterialsTab: () => void;
  onOpenShareModal: () => void;
}

/**
 * Header for an ordinary (non-builder) conversation: back link, breadcrumb
 * title, and the Materials / Share actions — inline on large screens, in a
 * three-dot `Dropdown` below `lg` (the same breakpoint at which the back link
 * appears and the chat page collapses its side panels). Builder conversations
 * use `BuilderChatHeader`, which offers neither action: Materials would put the
 * same proposal in two places beside the builder panel, and Share offers a
 * conversation whose record may not exist yet.
 */
export function ChatThreadHeader({
  title,
  activeTab,
  onNavigateBack,
  onToggleMaterialsTab,
  onOpenShareModal,
}: ChatThreadHeaderProps) {
  const isLargeScreen = useBreakpoint('lg');

  const materialsLabel =
    activeTab === 'materials' ? 'Conversation' : 'Materials';

  return (
    <div className='bg-bg-primary border-b border-border-secondary'>
      <div className='flex items-center justify-between px-3 sm:px-6 py-4'>
        <div className='flex items-center gap-2'>
          <Button
            color='tertiary'
            onClick={onNavigateBack}
            aria-label='Back to conversations'
            className='lg:hidden !p-1.5 -ml-1.5'
            iconLeading={
              <ArrowLeft className='size-5 text-tertiary' strokeWidth={2} />
            }
          />
          <p className='text-base leading-5 text-black dark:text-white'>
            <span className='font-normal hidden sm:inline text-neutral-600 dark:text-neutral-400'>
              Chat /{' '}
            </span>
            <span className='font-medium'>
              <TypewriterTitle title={title} />
            </span>
          </p>
        </div>

        <div className='flex items-center gap-3'>
          {isLargeScreen ? (
            <>
              <Button
                color={activeTab === 'materials' ? 'primary' : 'secondary'}
                size='sm'
                onClick={onToggleMaterialsTab}
              >
                {materialsLabel}
              </Button>
              <Button
                color='secondary'
                size='sm'
                iconLeading={Share01}
                onClick={onOpenShareModal}
              >
                Share
              </Button>
            </>
          ) : (
            <Dropdown.Root>
              <Button
                color='secondary'
                size='sm'
                iconLeading={DotsVertical}
                aria-label='More actions'
              />
              <Dropdown.Popover placement='bottom right' className='w-48'>
                {/* One-shot commands, not a picker — items render as
                    `menuitem`, not `menuitemradio`. */}
                <Dropdown.Menu selectionMode='none'>
                  <Dropdown.Item
                    id='materials'
                    label={materialsLabel}
                    onAction={onToggleMaterialsTab}
                  />
                  <Dropdown.Item
                    id='share'
                    label='Share'
                    icon={Share01}
                    onAction={onOpenShareModal}
                  />
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown.Root>
          )}
        </div>
      </div>
    </div>
  );
}
